import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, Pause, RotateCcw, XCircle, Download, FileSpreadsheet, FileText, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react'
import { jobsApi, exportApi, getApiErrorMessage } from '@/services/api'
import { formatDateTime, formatNumber, clampPercent } from '@/lib/format'
import { useState } from 'react'

type ExportFormat = 'csv' | 'xlsx' | 'json'

const statusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ожидает', color: '#f59e0b' },
  running: { label: 'Запущен', color: '#3b82f6' },
  paused: { label: 'Пауза', color: '#94a3b8' },
  completed: { label: 'Завершён', color: '#10b981' },
  failed: { label: 'Ошибка', color: '#ef4444' },
  cancelled: { label: 'Отменён', color: '#64748b' },
  waiting_user: { label: 'Ожидает юзера', color: '#f97316' },
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>()
  const jobId = Number(id)
  const qc = useQueryClient()
  const [exportMsg, setExportMsg] = useState('')

  const { data: job, isLoading, error } = useQuery({ queryKey: ['job', jobId], queryFn: () => jobsApi.get(jobId), refetchInterval: 5000 })
  const { data: logs } = useQuery({ queryKey: ['logs', jobId], queryFn: () => jobsApi.getLogs(jobId, 1, 100), refetchInterval: 5000 })
  const { data: exports } = useQuery({ queryKey: ['exports', jobId], queryFn: () => exportApi.list(jobId), refetchInterval: 10000 })

  const actionMutation = useMutation({
    mutationFn: async (action: 'start' | 'pause' | 'resume' | 'cancel') => {
      const apiMap = { start: jobsApi.start, pause: jobsApi.pause, resume: jobsApi.resume, cancel: jobsApi.cancel }
      return apiMap[action](jobId)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', jobId] }),
  })

  const exportMutation = useMutation({
    mutationFn: (f: ExportFormat) => exportApi.create(jobId, f),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['exports', jobId] })
      setExportMsg(`Экспорт ${data.format.toUpperCase()} создан: ${data.row_count} строк`)
      setTimeout(() => setExportMsg(''), 4000)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => jobsApi.delete(jobId),
    onSuccess: () => window.location.href = '/',
  })

  if (isLoading) return <div className="fade-in" style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
  if (error) return (
    <div className="fade-in" style={{ maxWidth: 600, margin: '80px auto' }}>
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <AlertCircle size={48} style={{ color: 'var(--danger)', marginBottom: 16 }} />
        <h3 style={{ marginBottom: 8 }}>Ошибка загрузки</h3>
        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{getApiErrorMessage(error)}</p>
        <Link to="/" className="btn btn-primary">На главную</Link>
      </div>
    </div>
  )
  if (!job) return null

  const cfg = statusConfig[job.status] || statusConfig.pending
  const pct = clampPercent(job.progress.progress_percent)
  const canStart = ['pending', 'paused', 'waiting_user'].includes(job.status)
  const canPause = job.status === 'running'
  const canCancel = !['completed', 'cancelled', 'failed'].includes(job.status)
  const canDelete = ['completed', 'cancelled', 'failed'].includes(job.status)

  return (
    <div className="fade-in" style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <Link to="/" className="btn btn-ghost"><ArrowLeft size={18} /></Link>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700 }}>{job.name || `Задача #${jobId}`}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
            <span className={`badge badge--${job.status}`}><span className="pulse-dot" />{cfg.label}</span>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Создана {formatDateTime(job.created_at)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canStart && <button className="btn btn-primary btn-sm" onClick={() => actionMutation.mutate('start')} disabled={actionMutation.isPending}><Play size={14} /> Запустить</button>}
          {canPause && <button className="btn btn-secondary btn-sm" onClick={() => actionMutation.mutate('pause')} disabled={actionMutation.isPending}><Pause size={14} /> Пауза</button>}
          {job.status === 'paused' && <button className="btn btn-primary btn-sm" onClick={() => actionMutation.mutate('resume')} disabled={actionMutation.isPending}><RotateCcw size={14} /> Продолжить</button>}
          {canCancel && <button className="btn btn-danger btn-sm" onClick={() => actionMutation.mutate('cancel')} disabled={actionMutation.isPending}><XCircle size={14} /> Отменить</button>}
          {canDelete && <button className="btn btn-ghost btn-sm" onClick={() => { if (confirm('Удалить задачу?')) deleteMutation.mutate() }} style={{ color: 'var(--danger)' }}>Удалить</button>}
        </div>
      </div>

      {actionMutation.isError && (
        <div style={{ padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: 'var(--danger)', marginBottom: 16, fontSize: 14 }}>
          {getApiErrorMessage(actionMutation.error)}
        </div>
      )}

      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Прогресс сбора</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, maxWidth: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.source_url}</div>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: cfg.color }}>{pct.toFixed(1)}%</div>
        </div>
        <div className="progress-bar" style={{ height: 12, marginBottom: 20 }}>
          <div className="progress-bar-fill" style={{ width: `${pct}%`, background: cfg.color === '#3b82f6' ? undefined : cfg.color }} />
        </div>
        <div className="grid-4">
          <div><div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Страниц</div><div style={{ fontSize: 20, fontWeight: 700 }}>{job.progress.processed_pages}<span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>/{job.progress.total_pages || '—'}</span></div></div>
          <div><div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Найдено</div><div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(job.progress.total_companies)}</div></div>
          <div style={{ color: 'var(--success)' }}><div style={{ fontSize: 12, marginBottom: 4 }}>Собрано</div><div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(job.progress.scraped_companies)}</div></div>
          <div style={{ color: 'var(--danger)' }}><div style={{ fontSize: 12, marginBottom: 4 }}>Ошибки</div><div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(job.progress.failed_companies)}</div></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: job.progress.scraped_companies > 0 ? '1fr 280px' : '1fr', gap: 16, marginBottom: 24 }}>
        {job.progress.scraped_companies > 0 && (
          <Link to={`/jobs/${jobId}/companies`} className="card card-interactive" style={{ textDecoration: 'none' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>📋 Собранные компании</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Просмотр и поиск по {formatNumber(job.progress.scraped_companies)} компаниям</div>
          </Link>
        )}
        <div className="card">
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Экспорт данных</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(['xlsx', 'csv', 'json'] as ExportFormat[]).map(f => (
              <button key={f} className="btn btn-secondary btn-sm" onClick={() => exportMutation.mutate(f)} disabled={exportMutation.isPending}>
                {f === 'xlsx' ? <FileSpreadsheet size={14} /> : <FileText size={14} />}
                {f.toUpperCase()}
              </button>
            ))}
            {exportMsg && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--success)' }}>
                <CheckCircle2 size={14} /> {exportMsg}
              </div>
            )}
            {exportMutation.isError && (
              <div style={{ fontSize: 13, color: 'var(--danger)' }}>Ошибка экспорта</div>
            )}
            {exports?.items && exports.items.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Готовые файлы:</div>
                {exports.items.map(e => (
                  <a key={e.id} href={exportApi.downloadUrl(e.id)} download className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textDecoration: 'none' }}>
                    <Download size={16} />
                    <span>Скачать {e.format.toUpperCase()}</span>
                    <span style={{ fontWeight: 400, opacity: 0.8 }}>({formatNumber(e.row_count)} строк)</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {job.error_message && (
        <div className="card" style={{ marginBottom: 24, borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.05)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger)', marginBottom: 8 }}>⚠ Ошибка</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{job.error_message}</div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Логи задачи</div>
          <button className="btn btn-ghost btn-sm" onClick={() => qc.invalidateQueries({ queryKey: ['logs', jobId] })}><RefreshCw size={14} /></button>
        </div>
        <div className="log-viewer">
          {logs?.items?.length ? logs.items.map(log => (
            <div key={log.id} className={`log-line log-line--${log.level.toLowerCase()}`}>
              <span className="log-time">{formatDateTime(log.created_at)}</span>
              {log.message}
            </div>
          )) : <div style={{ color: 'var(--text-muted)' }}>Логов пока нет</div>}
        </div>
      </div>
    </div>
  )
}
